import { supabase } from './supabaseClient';
import { CreditPackage, CreditTransaction, Payment, StripePaymentIntent } from '../types/credits';

export const getCreditPackages = async (): Promise<CreditPackage[]> => {
  const { data, error } = await supabase
    .from('credit_packages')
    .select('*')
    .eq('active', true)
    .order('sort_order', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch credit packages: ${error.message}`);
  }

  return data || [];
};

export const getCreditTransactions = async (userId: string, limit = 50): Promise<CreditTransaction[]> => {
  const { data, error } = await supabase
    .from('credit_transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw new Error(`Failed to fetch credit transactions: ${error.message}`);
  }

  return data || [];
};

export const createPaymentIntent = async (packageId: string): Promise<StripePaymentIntent> => {
  const { data, error } = await supabase.functions.invoke('create-payment-intent', {
    body: { package_id: packageId }
  });

  if (error) {
    throw new Error(`Failed to create payment intent: ${error.message}`);
  }

  return data;
};

export const confirmPayment = async (paymentIntentId: string): Promise<Payment> => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Find the credit transaction record
    console.log('Looking for transaction with paymentIntentId:', paymentIntentId, 'userId:', user.id);
    
    const { data: transaction, error: transactionError } = await supabase
      .from('credit_transactions')
      .select('*')
      .eq('stripe_payment_intent_id', paymentIntentId)
      .eq('user_id', user.id)
      .single();

    console.log('Transaction query result:', { transaction, transactionError });

    if (transactionError || !transaction) {
      // Let's also try to find any transaction with this payment intent ID
      const { data: allTransactions, error: allError } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('stripe_payment_intent_id', paymentIntentId);
      
      console.log('All transactions with this payment intent:', { allTransactions, allError });
      
      throw new Error(`Payment transaction not found. PaymentIntentId: ${paymentIntentId}, UserId: ${user.id}`);
    }

    // Check if transaction is already processed (type is PURCHASE)
    if (transaction.type === 'PURCHASE') {
      console.log('Transaction already processed, returning existing data');
      return {
        id: transaction.id,
        user_id: transaction.user_id,
        amount: transaction.amount,
        currency: 'EUR',
        status: 'COMPLETED' as const,
        created_at: transaction.created_at,
        updated_at: transaction.created_at
      };
    }

    // Update transaction type to PURCHASE and add credits
    const { error: updateError } = await supabase
      .from('credit_transactions')
      .update({ type: 'PURCHASE' })
      .eq('id', transaction.id);

    if (updateError) {
      console.error('Error updating transaction type:', updateError);
    }

    // Add credits to user's account using the existing RPC function
    const { error: creditsError } = await supabase.rpc('add_credits', {
      p_user_id: user.id,
      p_amount: transaction.amount,
      p_type: 'PURCHASE',
      p_description: `Credit purchase - ${transaction.description || 'Credit Package'}`
    });

    if (creditsError) {
      console.error('Error adding credits:', creditsError);
      // Don't fail payment confirmation if credits addition fails
    }


    return {
      id: transaction.id,
      user_id: transaction.user_id,
      amount: transaction.amount,
      currency: 'EUR',
      status: 'COMPLETED' as const,
      created_at: transaction.created_at,
      updated_at: transaction.created_at
    };

  } catch (error) {
    console.error('Error confirming payment:', error);
    throw new Error(`Failed to confirm payment: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const getPaymentHistory = async (userId: string, limit = 50): Promise<Payment[]> => {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw new Error(`Failed to fetch payment history: ${error.message}`);
  }

  return data || [];
};

// Get user's current credit balance
export const getUserCredits = async (userId: string): Promise<number> => {
  const { data, error } = await supabase
    .from('user_credits')
    .select('credits_balance')
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No credits record found, return 0
      return 0;
    }
    throw new Error(`Failed to fetch user credits: ${error.message}`);
  }

  return data?.credits_balance || 0;
};

// Check if user has access to a specific provider's contact details
export const hasContactAccess = async (clientId: string, providerId: string): Promise<boolean> => {
  try {
    console.log('hasContactAccess called with:', { clientId, providerId });
    
    const { data, error } = await supabase.rpc('check_contact_access', {
      p_client_id: clientId,
      p_provider_id: providerId
    });

    console.log('check_contact_access RPC result:', { data, error });

    if (error) {
      console.error('Error checking contact access:', error);
      return false;
    }

    const result = data || false;
    console.log('hasContactAccess returning:', result);
    return result;
  } catch (err) {
    console.error('Error checking contact access:', err);
    return false;
  }
};

// Purchase contact access using credits
export const purchaseContactAccess = async (providerId: string): Promise<{ success: boolean; message: string }> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  const clientId = user.id;
  const creditsRequired = 3;

  // Check if user has enough credits first
  const currentCredits = await getUserCredits(clientId);
  if (currentCredits < creditsRequired) {
    return { 
      success: false, 
      message: `Нямате достатъчно кредити. Необходими са ${creditsRequired} кредита, а имате ${currentCredits}.` 
    };
  }

  try {
    // Start a transaction to deduct credits and create contact access record
    const { data, error } = await supabase.rpc('purchase_contact_access', {
      p_client_id: clientId,
      p_provider_id: providerId,
      p_credits_required: creditsRequired
    });

    if (error) {
      console.error('RPC error:', error);
      throw new Error(`Failed to purchase contact access: ${error.message}`);
    }

    console.log('Purchase successful, RPC returned:', data);
    return { 
      success: true, 
      message: `Успешно закупихте достъп до контактните данни за ${creditsRequired} кредита!` 
    };
  } catch (error) {
    console.error('Error purchasing contact access:', error);
    return { 
      success: false, 
      message: 'Грешка при закупуване на достъп до контактните данни' 
    };
  }
};

// Mock data for credit packages (until we have real data in the database)
export const mockCreditPackages: CreditPackage[] = [
  {
    id: '1',
    name: 'Starter Pack',
    description: 'Perfect for getting started',
    credits_amount: 5,
    price: 2.99,
    active: true,
    sort_order: 1,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Professional Pack',
    description: 'Most popular choice',
    credits_amount: 15,
    price: 7.99,
    active: true,
    sort_order: 2,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: '3',
    name: 'Business Pack',
    description: 'For heavy users',
    credits_amount: 30,
    price: 14.99,
    active: true,
    sort_order: 3,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: '4',
    name: 'Enterprise Pack',
    description: 'Maximum value',
    credits_amount: 60,
    price: 24.99,
    active: true,
    sort_order: 4,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];
